// Phase 9.B — agentState derivation (design-review D-2).
//
// Load-bearing: this is the engine of the "Handling it" vs "Decision
// needed" UX. It is DERIVED, never stored. Pure + tested, ships before
// any UI consumes it. Evaluated TOP-DOWN; first match wins.

import type { LifecycleState } from "./types";

export type AgentState =
  | "escalated"
  | "blocked"
  | "needs_approval"
  | "acting"
  | "waiting"
  | "investigating"
  | "planning"
  | "resolved"
  | "observing";

// Lean input shapes — decoupled from Convex Docs, like the other pure
// modules. The Convex query populates these from existing tables.
export interface AgentActionInput {
  status: "queued" | "running" | "succeeded" | "failed" | "skipped";
  /** Evidence types this action is expected to yield (from tool contract). */
  produces?: string[];
  createdAt: number;
}

export interface ApprovalInput {
  status: "pending" | "approved" | "edited_and_approved" | "rejected" | "escalated" | "emergency_locked";
  createdAt: number;
}

export interface ExceptionInput {
  status: "open" | "in_progress" | "resolved" | "dismissed";
  createdAt: number;
}

export interface EvidenceInput {
  type: string;
  /** ISO; compared against action.createdAt (epoch ms). */
  occurredAt: string;
}

export interface AgentStateArgs {
  lifecycleState: LifecycleState;
  actions: AgentActionInput[];
  approvals: ApprovalInput[];
  exceptions: ExceptionInput[];
  evidence: EvidenceInput[];
}

const OPEN_EXCEPTION = new Set(["open", "in_progress"]);
const RESOLVED_LIFECYCLE = new Set<LifecycleState>(["delivered", "closed"]);

function latestAction(actions: AgentActionInput[]): AgentActionInput | undefined {
  return actions.length === 0
    ? undefined
    : actions.reduce((a, b) => (b.createdAt > a.createdAt ? b : a));
}

function earliestOpenException(exceptions: ExceptionInput[]): ExceptionInput | undefined {
  const open = exceptions.filter((e) => OPEN_EXCEPTION.has(e.status));
  return open.length === 0 ? undefined : open.reduce((a, b) => (b.createdAt < a.createdAt ? b : a));
}

/**
 * Derive the agent's operational state for one load. Top-down; first
 * match wins. Never throws; "observing" is the calm default.
 */
export function deriveAgentState(args: AgentStateArgs): AgentState {
  const hasPendingApproval = args.approvals.some((a) => a.status === "pending");
  const hasEscalation = args.approvals.some(
    (a) => a.status === "escalated" || a.status === "emergency_locked",
  );
  const last = latestAction(args.actions);
  const openExc = earliestOpenException(args.exceptions);

  // 1. escalated — a human owner is explicitly required
  if (hasEscalation) return "escalated";

  // 2. blocked — agent's last action failed and it hasn't asked for help yet
  if (last?.status === "failed" && !hasPendingApproval) return "blocked";

  // 3. needs_approval — a decision is waiting on a human
  if (hasPendingApproval) return "needs_approval";

  // 4. acting — agent is mid-execution
  if (last?.status === "running") return "acting";

  // 5. waiting — last action succeeded but its produced evidence hasn't arrived
  if (last?.status === "succeeded" && last.produces && last.produces.length > 0) {
    const producedSince = args.evidence.some(
      (e) => last.produces!.includes(e.type) && Date.parse(e.occurredAt) > last.createdAt,
    );
    if (!producedSince) return "waiting";
  }

  // 6/7. an open exception → investigating (acted since) vs planning (not yet)
  if (openExc) {
    const actedSinceException = args.actions.some((a) => a.createdAt >= openExc.createdAt);
    return actedSinceException ? "investigating" : "planning";
  }

  // 8. resolved — delivered/closed with nothing open
  if (RESOLVED_LIFECYCLE.has(args.lifecycleState) && !hasPendingApproval) {
    return "resolved";
  }

  // 9. observing — the calm steady state
  return "observing";
}

export type AgentStateLabel = "Handling it" | "Decision needed" | "Needs attention" | "Resolved";

/**
 * Render bucket. Spec-named buckets are honored exactly:
 * observing/waiting → "Handling it"; needs_approval/escalated →
 * "Decision needed". The remainder map sensibly.
 */
export function agentStateLabel(state: AgentState): AgentStateLabel {
  switch (state) {
    case "needs_approval":
    case "escalated":
      return "Decision needed";
    case "blocked":
      return "Needs attention";
    case "resolved":
      return "Resolved";
    case "observing":
    case "waiting":
    case "acting":
    case "investigating":
    case "planning":
      return "Handling it";
  }
}
